import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileText, Film, X, Loader2 } from "lucide-react";
import api from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;

export const fileUrl = (a) => (a.url?.startsWith("http") ? a.url : `${BASE}${a.url}`);

export const AttachmentThumb = ({ a, onRemove }) => {
  const isImg = (a.content_type || "").startsWith("image/");
  const isVid = (a.content_type || "").startsWith("video/");
  return (
    <div className="relative group rounded-xl border border-slate-200 bg-slate-50 overflow-hidden aspect-square" data-testid={`attachment-${a.id}`}>
      {isImg ? (
        <img src={fileUrl(a)} alt={a.original_filename} className="h-full w-full object-cover" />
      ) : (
        <a href={fileUrl(a)} target="_blank" rel="noreferrer" className="h-full w-full flex flex-col items-center justify-center gap-2 p-2 text-[#165a71]">
          {isVid ? <Film className="h-6 w-6" /> : <FileText className="h-6 w-6" />}
          <span className="text-[10px] text-center text-[#252A34]/70 line-clamp-2 break-all">{a.original_filename}</span>
        </a>
      )}
      {onRemove && (
        <button type="button" onClick={onRemove} className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-90 hover:bg-black" data-testid={`attachment-remove-${a.id}`}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

export default function AttachmentsStep({ attachments, onChange }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const upload = async (files) => {
    let list = [...attachments];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "request_attachment");
      try {
        setProgress({ name: file.name, pct: 0 });
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => setProgress({ name: file.name, pct: Math.round((e.loaded / (e.total || 1)) * 100) }),
        });
        list = [...list, { id: data.id, url: data.url, original_filename: data.original_filename, content_type: data.content_type }];
        onChange(list);
      } catch (err) {
        toast.error(err?.response?.data?.error || `Could not upload ${file.name}`);
      }
    }
    setProgress(null);
  };

  return (
    <div className="space-y-5" data-testid="attachments-step">
      <div>
        <div className="font-display text-xl font-semibold text-[#071b22]">Photos, videos & files</div>
        <p className="text-sm text-[#252A34]/65 mt-1">Requests with photos get more accurate estimates. Add the problem area, plans, or examples you like.</p>
      </div>
      <button type="button" onClick={() => inputRef.current?.click()} className="w-full rounded-2xl border-2 border-dashed border-[#165a71]/30 bg-[#165a71]/[0.03] hover:bg-[#165a71]/[0.06] transition-colors p-8 flex flex-col items-center gap-2 text-[#165a71]" data-testid="attachments-dropzone">
        <UploadCloud className="h-7 w-7" />
        <span className="font-semibold text-sm">Tap to add files</span>
        <span className="text-xs text-[#252A34]/60">Images, video (MP4/MOV) or PDF · up to 50 MB each</span>
      </button>
      <input ref={inputRef} type="file" multiple accept="image/*,video/mp4,video/quicktime,video/webm,application/pdf" className="hidden" onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }} data-testid="attachments-input" />
      {progress && (
        <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs flex items-center gap-3" data-testid="upload-progress">
          <Loader2 className="h-4 w-4 animate-spin text-[#165a71]" />
          <span className="truncate flex-1">{progress.name}</span>
          <div className="w-32 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#165a71] transition-[width]" style={{ width: `${progress.pct}%` }} /></div>
          <span className="tabular-nums w-9 text-right">{progress.pct}%</span>
        </div>
      )}
      {attachments.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {attachments.map((a) => <AttachmentThumb key={a.id} a={a} onRemove={() => onChange(attachments.filter((x) => x.id !== a.id))} />)}
        </div>
      )}
    </div>
  );
}
